import React, { useState } from 'react';
import { ChevronDown, LogOut, Menu, Settings } from 'lucide-react';
import AdminNotificationBell from '../AdminNotificationBell';
import { useAuth } from '../../hooks/useAuth';
import { getAdminRoleLabel } from '../../config/permissions';
import { Avatar } from '../ui/Avatar';
import Breadcrumbs from '../ui/Breadcrumbs';
import type { BreadcrumbItem } from '../ui/Breadcrumbs';
import SettingsModal from './SettingsModal';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn';

export type { BreadcrumbItem };

interface HeaderProps {
  onMenuClick: () => void;
  breadcrumbs?: BreadcrumbItem[];
  onBreadcrumbClick?: (path: string) => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, breadcrumbs = [], onBreadcrumbClick }) => {
  const { user, logout } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <>
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-background px-4 sm:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
          <Button variant="ghost" size="icon" onClick={onMenuClick} className="shrink-0 lg:hidden" aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </Button>

          {breadcrumbs.length > 0 && (
            <Breadcrumbs
              items={breadcrumbs}
              onNavigate={(path) => onBreadcrumbClick?.(path)}
              className="hidden min-w-0 sm:flex"
            />
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <AdminNotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-auto max-w-[min(100%,14rem)] shrink-0 gap-2 px-2 py-1.5 sm:max-w-xs"
              aria-label="Account menu"
            >
              <Avatar user={user} size="sm" deterministicColor />
              <div className="hidden min-w-0 flex-1 flex-col items-start text-left sm:flex">
                <span className="w-full truncate text-sm font-medium leading-tight">
                  {user?.name || user?.email}
                </span>
                <span className="w-full truncate text-xs leading-tight text-muted-foreground">
                  {getAdminRoleLabel(user?.role)}
                </span>
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="truncate text-sm font-medium leading-none">{user?.name || user?.email}</p>
                <p className="truncate text-xs leading-none text-muted-foreground">{getAdminRoleLabel(user?.role)}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setSettingsOpen(true);
              }}
            >
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                logout();
              }}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </header>

      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
};

export default Header;
